import { fromPromise, setup, assign } from "xstate";

import { RefreshController } from "./controller";

export type RefreshMachineContext = {
  lastRefreshedAt: Date;
  autoRefreshInterval: number;
  controller: RefreshController;
};

export type RefreshMachineInput = {
  autoRefreshInterval: number;
};

export type RefreshMachineEvent =
  | { type: "REFRESH_NOW" }
  | { type: "REFRESH_ACTIONS_CHANGED" };

export const refreshMachine = setup({
  types: {
    context: {} as RefreshMachineContext,
    input: {} as RefreshMachineInput,
    events: {} as RefreshMachineEvent,
  },
  actors: {
    refresh: fromPromise<void, { controller: RefreshController }>(async ({ input }) => {
      await input.controller.refresh();
    }),
  },
  delays: {
    AUTO_REFRESH_TIMER: ({ context }) => context.autoRefreshInterval,
  },
}).createMachine({
  initial: "inactive",
  context: ({ input, self }) => ({
    ...input,
    lastRefreshedAt: new Date(),
    controller: new RefreshController({
      onActionsChanged: () => self.send({ type: "REFRESH_ACTIONS_CHANGED" }),
    }),
  }),
  states: {
    inactive: {
      on: {
        REFRESH_ACTIONS_CHANGED: {
          guard: ({ context }) => context.controller.actions.size > 0,
          target: "active",
        },
      },
    },
    active: {
      on: {
        REFRESH_ACTIONS_CHANGED: {
          target: "inactive",
          guard: ({ context }) => context.controller.actions.size === 0,
        },
      },
      initial: "pending",
      states: {
        pending: {
          entry: assign({
            lastRefreshedAt: () => new Date(),
          }),
          on: {
            REFRESH_NOW: {
              target: "refreshing",
            },
          },
          after: {
            AUTO_REFRESH_TIMER: {
              target: "refreshing",
            },
          },
        },
        refreshing: {
          invoke: {
            src: "refresh",
            input: ({ context }) => ({ controller: context.controller }),
            onDone: {
              target: "pending",
            },
            onError: {
              target: "pending",
              actions: ({ event }) =>
                console.error(
                  "TODO: handle onError in refreshMachine refreshing",
                  event.error,
                ),
            },
          },
        },
      },
    },
  },
});
